import { useMemo, useSyncExternalStore } from "react";
import MiniEvent from "./mini-event";

/**
 * `localStorage`/`sessionStorage` access that survives the browser saying no,
 * plus a `useState`-shaped hook on top of it.
 *
 * Why not `react-use`'s `useLocalStorage`: that one keeps its value in plain
 * component state, so two components reading the same key never learn about
 * each other's writes — one of them silently renders a stale value until it
 * remounts. Here every key is backed by a single module-level store that all
 * of its readers subscribe to, so a write through any one hook re-renders
 * every consumer of that key.
 *
 * Not covered: writes from *other tabs* (the native `storage` event) and writes
 * made by calling `localStorage.setItem` directly — an entry reads the
 * underlying storage once, when it is first created, and tracks its own value
 * from then on.
 */

type StorageType = "local" | "session";

type StorageSetter<T> = (
  setter: T | ((prevState: T | undefined) => T),
  options?: { dontPersist?: boolean },
) => void;

// Accessing `window.localStorage` can throw outright — Safari in private mode,
// or an embedding page whose cookie/storage policy blocks it — so every access
// below is guarded and degrades to "no storage available" rather than crashing
// the render.
const getStorageInstance = (type: StorageType): Storage | undefined => {
  try {
    return type === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined;
  }
};

const safeGet = <T>(key: string, type: StorageType): T | undefined => {
  const storage = getStorageInstance(type);
  if (!storage) return undefined;
  try {
    const content = storage.getItem(key);
    // `undefined` means "nothing stored"; a stored `null` stays `null`, so a
    // caller can tell an explicit null apart from an absent key.
    return content === null ? undefined : JSON.parse(content);
  } catch {
    // Unparseable leftovers (a value written before a format change, or by
    // something that did not use JSON) read as absent.
    return undefined;
  }
};

const safeSet = <T>(key: string, val: T, type: StorageType): boolean => {
  const storage = getStorageInstance(type);
  if (!storage) return false;
  try {
    storage.setItem(key, JSON.stringify(val));
    return true;
  } catch {
    // Quota exceeded, or storage blocked. The in-memory value still updates —
    // the state just does not outlive the session.
    return false;
  }
};

const safeRemove = (key: string, type: StorageType): boolean => {
  const storage = getStorageInstance(type);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

// Broadcast whenever a value changes, so entries for the same key stay in
// sync. `by` is the entry that caused the change and lets it skip its own echo.
type StorageChange = {
  key: string;
  type: StorageType;
  val: unknown;
  by: unknown;
};
const storageChangeEvent = new MiniEvent<[StorageChange]>();

/**
 * The single source of truth for one key: holds the current value, notifies its
 * subscribers, and writes through to the underlying storage.
 */
class StorageEntry<T> {
  private listeners = new Set<() => void>();
  private snapshot: T | undefined;
  private key: string;
  private type: StorageType;

  constructor(key: string, type: StorageType) {
    this.key = key;
    this.type = type;
    this.snapshot = safeGet<T>(key, type);
  }

  subscribe = (callback: () => void) => {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  };

  // Identity-stable between changes, which is what lets `useSyncExternalStore`
  // skip re-rendering subscribers that did not actually change.
  getSnapshot = () => this.snapshot;

  set: StorageSetter<T> = (setter, { dontPersist } = {}) => {
    const value = setter instanceof Function ? setter(this.snapshot) : setter;
    this.snapshot = value;
    // `dontPersist` keeps a value live for this page load only — useful for a
    // preview/what-if state that should not stick around.
    if (!dontPersist) safeSet(this.key, value, this.type);
    this.emitChange(value);
  };

  clear = () => {
    safeRemove(this.key, this.type);
    this.snapshot = undefined;
    this.emitChange(undefined);
  };

  /** Adopt a value written elsewhere, without writing it back out again. */
  updateExternal = (value: T | undefined) => {
    this.snapshot = value;
    this.notify();
  };

  private emitChange(val: T | undefined) {
    storageChangeEvent.emit({ key: this.key, type: this.type, val, by: this });
    this.notify();
  }

  private notify() {
    this.listeners.forEach((listener) => listener());
  }
}

// One entry per key, so every component reading that key shares one value.
// Entries are never evicted — they are tiny, and dropping one while a component
// still holds it would split the state again. Keys are expected to be a bounded
// set (per route, per spark, …), not unbounded per-item ids.
const entries = new Map<string, StorageEntry<unknown>>();

const entryCacheKey = (key: string, type: StorageType) => `${type}:${key}`;

storageChangeEvent.addListener(({ key, type, val, by }) => {
  const entry = entries.get(entryCacheKey(key, type));
  // Skip the entry that caused the change — it already holds the new value.
  if (entry && entry !== by) entry.updateExternal(val);
});

const getEntry = <T>(key: string, type: StorageType): StorageEntry<T> => {
  const cacheKey = entryCacheKey(key, type);
  let entry = entries.get(cacheKey);
  if (!entry) {
    entry = new StorageEntry<unknown>(key, type);
    entries.set(cacheKey, entry);
  }
  return entry as StorageEntry<T>;
};

/**
 * `useState`, persisted. Every component using the same key shares one value
 * and re-renders together.
 *
 * ```ts
 * const [phaseIndex, setPhaseIndex] = useStorageState<number>(key, 0);
 * const [draft, setDraft, { clear }] = useStorageState<Draft>(key, undefined, {
 *   type: "session",
 * });
 * ```
 *
 * `defaultVal` is a render-time fallback, not an initial write: nothing is
 * stored until something calls the setter.
 */
export function useStorageState<T, Fallback = T>(
  key: string,
  defaultVal: Fallback,
  options?: { type?: StorageType },
): readonly [T | Fallback, StorageSetter<T>, { clear: () => void }];
export function useStorageState<T>(
  key: string,
  defaultVal?: undefined,
  options?: { type?: StorageType },
): readonly [T | undefined, StorageSetter<T>, { clear: () => void }];
export function useStorageState<T>(
  key: string,
  defaultVal?: unknown,
  { type = "local" }: { type?: StorageType } = {},
) {
  const entry = useMemo(() => getEntry<T>(key, type), [key, type]);
  const val = useSyncExternalStore(entry.subscribe, entry.getSnapshot);

  return [
    val === undefined ? defaultVal : val,
    entry.set,
    { clear: entry.clear },
  ] as const;
}
